import React from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { POI_TYPES } from '../../../../../constants/poiTypes';
import { LoadingSpinner } from '../../../../../components/common/LoadingSpinner';
import { logError, logInfo } from "../../../../../utils/logger";

// Get API key directly from environment variable
const GOOGLE_MAPS_API_KEY = 'AIzaSyAdNYCigSTxjhN3zL39QmB6H4nL5-zku7A';

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: [
    {
      featureType: "administrative",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "landscape",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "road",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "water",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    }
  ]
};

const libraries = ['places', 'geometry'];

export const TimelineMap = ({ route, pois = [], onLoad }) => {
  const [isLoaded, setIsLoaded] = React.useState(!!window.google?.maps);
  const [loadError, setLoadError] = React.useState(null);
  const mapRef = React.useRef(null);
  
  // Check if Google Maps is loaded
  React.useEffect(() => {
    if (window.google?.maps) {
      setIsLoaded(true);
    } else {
      // Wait for Google Maps to load from the main script
      const checkGoogleMaps = setInterval(() => {
        if (window.google?.maps) {
          setIsLoaded(true);
          clearInterval(checkGoogleMaps);
        }
      }, 100);
      
      // Set a timeout to prevent infinite checking
      setTimeout(() => {
        if (!window.google?.maps) {
          setLoadError(new Error('Google Maps failed to load'));
          clearInterval(checkGoogleMaps);
        }
      }, 10000);
      
      return () => clearInterval(checkGoogleMaps);
    }
  }, []);

  if (loadError) {
    logError('Failed to load Google Maps:', loadError);
    return (
      <div className="m-4 bg-[#2A2B2E] rounded-lg overflow-hidden">
        <div className="h-[400px] flex items-center justify-center">
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
            Error loading Google Maps
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="m-4 bg-[#2A2B2E] rounded-lg overflow-hidden">
        <div className="h-[400px] flex flex-col items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <LoadingSpinner />
            <p className="text-white">Loading Google Maps...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleMapLoad = (map) => {
    logInfo('Timeline map loaded successfully');
    onLoad(map);
    
    // Set initial bounds if route is available
    if (route?.trackPoints?.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      route.trackPoints.forEach(point => {
        if (isValidCoordinate(Number(point.latitude), Number(point.longitude))) {
          bounds.extend({
            lat: Number(point.latitude),
            lng: Number(point.longitude)
          });
        }
      });
      map.fitBounds(bounds);
    }
  };

  return (
    <div className="m-4 bg-[#2A2B2E] rounded-lg overflow-hidden">
      <div className="h-[400px] relative">
        {pois.length > 0 && (
          <div className="absolute top-4 right-4 z-10 bg-white/90 px-3 py-1 rounded text-sm text-gray-600 shadow-sm">
            {pois.length} POIs
          </div>
        )}
        <div className="h-full w-full">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            zoom={12}
            center={getInitialCenter(route, pois)}
            onLoad={handleMapLoad}
            options={mapOptions}
          >
            {/* Route Polyline */}
            {route?.trackPoints?.length > 0 && (
              <Polyline
                path={getValidTrackPoints(route.trackPoints)}
                options={{
                  strokeColor: route.color || '#3B82F6',
                  strokeWeight: 5,
                  strokeOpacity: 0.8
                }}
              />
            )}
            
            {/* POI Markers */}
            {pois?.filter(poi => poi?.coordinates && (!route || poi.routeId === route?.id)).map(poi => {
              const poiType = POI_TYPES[poi.type] || POI_TYPES.Custom;
              const lat = Number(poi.coordinates.latitude);
              const lng = Number(poi.coordinates.longitude);
              
              if (!isValidCoordinate(lat, lng)) return null;
              
              return (
                <Marker
                  key={poi.id}
                  position={{ lat, lng }}
                  title={poi.notes || poi.id}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: poiType.color,
                    fillOpacity: 0.8,
                    strokeColor: '#FFFFFF',
                    strokeWeight: 2,
                    scale: 8
                  }}
                />
              );
            })}
          </GoogleMap>
        </div>
      </div>
     
     {/* Remove API Key display */}
     <style>{`
       .gm-style-cc {
         display: none !important;
       }
       .gmnoprint.gm-style-cc {
         display: none !important;
       }
       .gm-style div[style*="bottom: 0px"] {
         display: none !important;
       }
       div[style*="background-color: rgba(0, 0, 0, 0.5)"] {
         display: none !important;
       }
       div[style*="color: rgb(255, 255, 255)"] {
         display: none !important;
       }
       .gm-style-pbc {
         display: none !important;
       }
     `}</style>
    </div>
  );
};

// Helper functions
const getInitialCenter = (route, pois) => {
  if (route?.trackPoints?.[0]) {
    const lat = Number(route.trackPoints[0].latitude);
    const lng = Number(route.trackPoints[0].longitude);
    if (isValidCoordinate(lat, lng)) {
      return { lat, lng };
    }
  } else if (pois?.length > 0) {
    // If no route but we have POIs, center on first POI
    const firstPOI = pois[0];
    const lat = Number(firstPOI.coordinates?.latitude);
    const lng = Number(firstPOI.coordinates?.longitude);
    if (isValidCoordinate(lat, lng)) {
      return { lat, lng };
    }
  }
  return { lat: 45.5017, lng: -73.5673 }; // Default center
};

const getValidTrackPoints = (trackPoints) => {
  if (!Array.isArray(trackPoints)) return [];
  return trackPoints
    .map(point => {
      const lat = Number(point.latitude);
      const lng = Number(point.longitude);
      return isValidCoordinate(lat, lng) ? { lat, lng } : null;
    })
    .filter(Boolean);
};

const renderMarker = (coordinates, iconOptions, key = '') => {
  const lat = Number(coordinates.latitude);
  const lng = Number(coordinates.longitude);
  
  if (!isValidCoordinate(lat, lng)) return null;
  
  return (
    <Marker
      key={key}
      position={{ lat, lng }}
      options={{
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillOpacity: 1,
          strokeWeight: 2,
          strokeColor: '#FFFFFF',
          ...iconOptions
        }
      }}
    />
  );
};

const isValidCoordinate = (lat, lng) => {
  return (
    typeof lat === 'number' && 
    typeof lng === 'number' && 
    !isNaN(lat) && 
    !isNaN(lng) && 
    isFinite(lat) && 
    isFinite(lng) &&
    lat >= -90 && 
    lat <= 90 && 
    lng >= -180 && 
    lng <= 180
  );
};